import { BlogArticle, BlogTakeaways } from "../BlogArticle";
import { getBlogSummary } from "../blogIndex";

const post = getBlogSummary("3")!;
const lespaGalleryImages = [
  {
    src: "/assets/images/blog/lespa-time-machine-gallery-1.jpg",
    alt: "Another childhood drawing by Ahmad Danial",
    caption:
      "Another drawing from when I was small, showing the same habit of imagining whole scenes.",
  },
  {
    src: "/assets/images/blog/lespa-time-machine-gallery-2.jpg",
    alt: "A childhood drawing by Ahmad Danial from around the same age",
    caption:
      "One more old drawing that reminds me how much I liked turning ideas into pictures.",
  },
];

export default function TheTimeITriedToSellATimeMachine() {
  return (
    <BlogArticle post={post}>
      <p>
        When I was 6 years old, I joined a small drawing competition. The theme
        was about the future and what we thought it would be like.
      </p>

      <p>
        Most of the other kids drew themselves in the future. Some drew
        themselves as astronauts flying in space. Some drew themselves living in
        giant houses with swimming pools. Others drew themselves playing happily
        with lots of pets.
      </p>

      <p>My drawing was... a little different.</p>

      <p>
        Instead of drawing myself, I drew an entire city. The city was full of
        people using a product called Lespa. According to 6-year-old me, Lespa
        was a time machine that I was going to invent in the future.
      </p>

      <p>
        Looking back at the drawing now, I find it really funny. I did not just
        draw the time machine itself. I actually imagined it as a real product
        that everyone could buy.
      </p>

      <p>
        There were people walking around holding Lespa boxes. Some were leaving
        the Lespa shop carrying their new purchase. Some were unboxing their
        Lespa and getting ready to use it. The streets were busy with people
        using Lespa as if time travel had become a normal part of everyday life.
      </p>

      <p>
        I even designed a logo for the company and put it on the shop building.
        Apparently, 6-year-old me did not just want to invent a time machine. I
        wanted to build an entire business around it!
      </p>

      <p>
        I did not win the competition, but my drawing got a lot of attention
        from the judges because it was so different from the others. While many
        kids imagined what they would become in the future, I imagined a future
        invention and a whole world built around it.
      </p>

      <p>
        Now that I look at the drawing years later, I am both proud and amused.
        It is one of those moments that reminds me how big my imagination was
        when I was younger.
      </p>

      <p>
        Who knows? Maybe Lespa will not stay a drawing forever. Okay, maybe the
        time machine part is a little difficult. But it is still fun to look
        back and see what 6-year-old me dreamed about.
      </p>

      <section className="space-y-4">
        <h2>Here&rsquo;s the original drawing</h2>
        <p>
          This is the actual drawing from the competition. I like that it was
          not just one time machine in a corner. It was a whole world where
          people could walk into a shop, buy Lespa, carry the box home, and
          treat time travel like a normal thing.
        </p>
        <figure className="rounded-2xl overflow-hidden bg-white border border-stem-sand">
          <img
            src="/assets/images/blog/lespa-time-machine-drawing.jpg"
            alt="The original Lespa time machine city drawing from the future-themed competition"
            className="w-full h-auto"
          />
          <figcaption className="px-5 py-4 text-sm text-stem-gray">
            The original Lespa drawing, complete with the imaginary time machine
            product, shop, logo, boxes, and a very busy future city.
          </figcaption>
        </figure>
        <p>
          Even without a working time machine, I still like the idea behind
          Lespa: imagining something big, giving it a name, designing a logo,
          and thinking about how people might use it.
        </p>
      </section>

      <section className="space-y-5">
        <div>
          <h2>Other Drawings From When I Was Small</h2>
          <p>
            These are not Lespa drawings, but they are from around the same time
            when I was still drawing lots of big imaginary scenes. Looking at
            them together makes me realise that I have always enjoyed turning
            random ideas into little worlds on paper.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lespaGalleryImages.map((image) => (
            <figure
              key={image.src}
              className="rounded-2xl overflow-hidden bg-white border border-stem-sand"
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className="w-full aspect-[4/3] object-cover"
              />
              <figcaption className="px-5 py-4 text-sm text-stem-gray">
                {image.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <BlogTakeaways
        items={[
          "My imagination was already thinking about products and businesses when I was 6.",
          "A different idea can stand out even if it does not win the competition.",
          "Naming an idea and designing a logo can make it feel more real.",
          "Old drawings can remind us how freely we used to dream.",
        ]}
      />
    </BlogArticle>
  );
}
